import React, { useEffect, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import {
  addShapeProcessor,
  useAddOrUpdateFarmFeatureFlagAdmin,
  useGetAllFeatureFlags,
  useGetDataSubmissionDates,
  useGetFarmDataSubmissionStatus,
  useGetFarmFeatureFlags,
  useToggleFarmFootprinting,
  useUpdateFarmDataSubmissionAdmin,
} from "utils";

import { FEATURE_FLAG_NAMES, GET_FARM_DATA_SUBMISSION_STATUS } from "consts";

import { FarmResponse } from "models";

import { AdminSection, Button, Checkbox, Flex, Modal, Switch, TooltipWrapper, Typography, Message as message } from "components";

type FarmWithDealId = FarmResponse & {
  dealId?: string | null;
  test?: boolean;
  footprintingEnabled?: boolean;
};

interface FarmAdminTabProps {
  farm: FarmWithDealId | false | null;
  updateFarmDeal: () => void;
  updatingDeal: boolean;
  setShowModal: (show: boolean) => void;
  isTest: boolean;
  handleTestChecked: (checked: boolean) => void;
  loadFarm: () => void;
}

const isValidFarm = (farm: FarmWithDealId | false | null): farm is FarmWithDealId => farm !== false && farm !== null;

export const FarmAdminTab = ({ farm, updateFarmDeal, updatingDeal, setShowModal, isTest, handleTestChecked, loadFarm }: FarmAdminTabProps) => {
  const [togglingFootprinting, setTogglingFootprinting] = useState(false);

  const queryClient = useQueryClient();
  const farmId = isValidFarm(farm) ? farm.id : "";
  const { data: submissionStatusValue, isPending: loadingSubmissionStatusValue } = useGetFarmDataSubmissionStatus({
    farmId,
    isAdmin: true,
  });

  const { data: dataSubmissionDates, isPending: isPendingDataSubmissionDates } = useGetDataSubmissionDates({
    farmId: isValidFarm(farm) ? farm.id : undefined,
  });
  const { data: allFeatureFlags, isLoading: isLoadingAllFeatureFlags } = useGetAllFeatureFlags();
  const { data: farmFeatureFlags, isLoading: isLoadingFarmFeatureFlags } = useGetFarmFeatureFlags({ farmId });
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);

  const { mutateAsync: updateFarmDataSubmissionAdmin, isPending: isPendingUpdateFarmDataSubmissionAdmin } = useUpdateFarmDataSubmissionAdmin();
  const { mutateAsync: addOrUpdateFarmFeatureFlagAdmin } = useAddOrUpdateFarmFeatureFlagAdmin({ farmId });

  const { mutateAsync: toggleFarmFootprinting, isPending: isPendingToggleFarmFootprinting } = useToggleFarmFootprinting({ farmId });

  const isSubmissionPeriodOpen = dataSubmissionDates?.isSubmissionPeriodOpen;

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: [GET_FARM_DATA_SUBMISSION_STATUS, farmId] });
    addShapeProcessor();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChangeDataSubmissionState = async (): Promise<void> => {
    if (isValidFarm(farm) && farm.id) {
      const updateFarmDataSubmissionAdminResponse = await updateFarmDataSubmissionAdmin({ farmId: farm.id, submissionState: false });
      if (updateFarmDataSubmissionAdminResponse.success) message({ icon: "tick", content: updateFarmDataSubmissionAdminResponse.message });
      else message({ icon: "error", content: updateFarmDataSubmissionAdminResponse.message });
    } else {
      message({ icon: "error", content: "There is no farm" });
    }
    setShowConfirmationModal(false);
  };

  const handleUpdateFarmFeatureFlag = async (featureFlagId: string, enabledOnFarm: boolean): Promise<void> => {
    try {
      await addOrUpdateFarmFeatureFlagAdmin({ farmId, featureFlagId, enabledOnFarm });
      message({ icon: "tick", content: "Feature flag updated successfully" });
    } catch {
      message({ icon: "error", content: "Failed to update feature flag" });
    }
  };

  const handleToggleFootprinting = async (enabled: boolean): Promise<void> => {
    if (!farmId) {
      message({ icon: "error", content: "There is no farm id" });
      return;
    }
    setTogglingFootprinting(true);
    try {
      await toggleFarmFootprinting({ farmId, footprintingEnabled: enabled });
      message({ icon: "tick", content: `Footprinting ${enabled ? "enabled" : "disabled"} for this farm` });
      loadFarm();
    } catch {
      message({ icon: "error", content: "Failed to toggle footprinting" });
    } finally {
      setTogglingFootprinting(false);
    }
  };
  return (
    farm && (
      <>
        <AdminSection
          headline="Test farm"
          description={`This farm is ${isTest ? "marked as a test farm" : "not marked as a test farm"}. You can update it here.`}
          extra={
            <Checkbox checked={isTest} onChange={(e) => handleTestChecked(e.target.checked)}>
              Test farm
            </Checkbox>
          }
        />
        <AdminSection
          headline="Pipedrive"
          description={
            farm.dealId
              ? "This farm has a deal associated with it. You can update the Pipedrive deal data from here."
              : "This farm does not have Pipedrive deal associated with it. You can link a deal here."
          }
          extra={
            farm.dealId ? (
              <Button type="primary" onClick={updateFarmDeal} size="small" loading={updatingDeal}>
                Update deal
              </Button>
            ) : (
              <Button type="primary" onClick={() => setShowModal(true)} size="small" loading={updatingDeal}>
                Link deal
              </Button>
            )
          }
        />
        <AdminSection
          headline="Shapefile upload"
          // The previous implementation was broken and has therefore been removed.
          // If necessary, it can be added back in, using the same logic as the shapefile upload for the user.
          description="Currently not available as admin feature. Users can upload shapefiles for their own farms."
        />
        <AdminSection
          headline="Undo Submission"
          description="Only enabled if the farmer has already submitted data and the submission period is open. Warning: It cannot be undone."
          extra={
            <Button
              type="primary"
              size="small"
              disabled={
                !submissionStatusValue?.isSubmitted ||
                !isSubmissionPeriodOpen ||
                (submissionStatusValue &&
                  "payoutApproved" in submissionStatusValue &&
                  typeof submissionStatusValue.payoutApproved === "boolean" &&
                  submissionStatusValue.payoutApproved)
              }
              loading={loadingSubmissionStatusValue || isPendingUpdateFarmDataSubmissionAdmin || isPendingDataSubmissionDates}
              onClick={() => setShowConfirmationModal(true)}
            >
              Undo Submission
            </Button>
          }
        />

        <AdminSection
          headline="Footprinting"
          description={`Farm-level footprinting is currently ${
            isValidFarm(farm) && farm.footprintingEnabled ? "enabled" : "disabled"
          }. When enabled, the farm will have access to footprinting features regardless of the carbon program configuration.`}
          extra={
            <Switch
              checked={isValidFarm(farm) && !!farm.footprintingEnabled}
              onChange={(enabled) => handleToggleFootprinting(enabled)}
              disabled={togglingFootprinting || isPendingToggleFarmFootprinting}
            />
          }
        />

        <AdminSection
          headline="Feature Flags"
          description={"Enable specific features for this farm."}
          extra={
            <Flex align="flex-end" justify="flex-end" gap="0.5rem" vertical>
              {!isLoadingAllFeatureFlags &&
                !isLoadingFarmFeatureFlags &&
                allFeatureFlags?.map((flag) => {
                  const farmFeatureFlag = farmFeatureFlags?.find((f) => f.featureFlagId === flag.id);
                  const isForceEnabled = flag.enabledGlobally || farmFeatureFlag?.enabledForFarmCountry || farmFeatureFlag?.enabledOnProgram;
                  const isEnabled = isForceEnabled || farmFeatureFlag?.enabledOnFarm;

                  const enabledLocations: string[] = [];
                  if (flag.enabledGlobally) enabledLocations.push("globally");
                  if (farmFeatureFlag?.enabledForFarmCountry) enabledLocations.push("for this farm's country");
                  if (farmFeatureFlag?.enabledOnProgram) enabledLocations.push("on program level");
                  const tooltipTitle =
                    isForceEnabled && enabledLocations.length > 0
                      ? `This feature is enabled ${enabledLocations.join(", ")} and cannot be disabled for this farm.`
                      : "";

                  return (
                    <Flex key={flag.id} align="center" gap="0.5rem">
                      <Typography variant="paragraph-m" style={{ fontSize: "0.875rem" }} color="grey-600">
                        {`${FEATURE_FLAG_NAMES[flag.slug as keyof typeof FEATURE_FLAG_NAMES] || flag.slug}: ${isEnabled ? "Enabled" : "Disabled"}`}
                      </Typography>
                      <TooltipWrapper tooltip={{ title: tooltipTitle }}>
                        <Switch checked={isEnabled} onChange={(enabled) => handleUpdateFarmFeatureFlag(flag.id, enabled)} disabled={isForceEnabled} />
                      </TooltipWrapper>
                    </Flex>
                  );
                })}
            </Flex>
          }
        />
        <Modal
          cancelText={"Cancel"}
          className="confirm-baseline-drawer"
          description={
            "Are you sure you want to revert this submission?\n" +
            "This action will set the user's status back to 'not submitted' and reopen their data entry.\n" +
            "Only the user can resubmit — the admin cannot submit for them.\n" +
            "This action cannot be undone."
          }
          onCancel={() => setShowConfirmationModal(false)}
          onOk={handleChangeDataSubmissionState}
          okText={"Undo Submission"}
          showModal={showConfirmationModal}
          textColour="deep-black-text"
          title={"Undo Submission"}
          loading={isPendingUpdateFarmDataSubmissionAdmin}
        />
      </>
    )
  );
};
